/**
 * Umbuchen (G-13): Planzeile → neue Schicht und/oder neue Stellenzuordnung
 * (PATCH /shift-plan/:id, Abwesenheits-Guard → 409-Toast).
 *
 * Zwei Einstiege: fixe Planzeile (Klick im Planraster) oder Planzeilen-Auswahl
 * des Tages (Aktionsleiste des Anwesenheitsboards).
 */

import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ArrowLeftRight } from "lucide-react"
import { toast } from "sonner"
import { PlanEntryPicker } from "@/components/attendance/PlanEntryPicker"
import { SwapShiftDialog } from "@/components/planning/SwapShiftDialog"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { fmtDate } from "@/lib/format"
import { api, apiError } from "@/services/api"
import type { ShiftPlanEntry } from "@/types"

interface RebookSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fest vorgegebene Planzeile (Planraster); sonst Auswahl ueber `date` */
  fixedEntry?: ShiftPlanEntry | null
  /** Stichtag fuer die Planzeilen-Auswahl (Board-Modus) */
  date?: string
}

export function RebookSheet({ open, onOpenChange, fixedEntry, date }: RebookSheetProps) {
  const queryClient = useQueryClient()
  const [picked, setPicked] = useState<ShiftPlanEntry | null>(null)
  const [shiftId, setShiftId] = useState("")
  const [employeePositionId, setEmployeePositionId] = useState("")
  const [note, setNote] = useState("")
  // Beim Öffnen des Tausch-Dialogs die Planzeile festhalten: der Umbuch-Dialog
  // schließt dabei, wodurch `fixedEntry`/`picked` wegfallen würden.
  const [swapEntry, setSwapEntry] = useState<ShiftPlanEntry | null>(null)

  const entry = fixedEntry ?? picked

  useEffect(() => {
    if (open) {
      setPicked(null)
      setShiftId(fixedEntry?.shiftId ?? "")
      setEmployeePositionId(fixedEntry?.employeePositionId ?? "")
      setNote(fixedEntry?.note ?? "")
    }
    // Vorbelegung ist beim Oeffnen fix
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Bei Auswahl im Picker die aktuellen Werte der Zeile vorbelegen
  function onPick(e: ShiftPlanEntry | null) {
    setPicked(e)
    setShiftId(e?.shiftId ?? "")
    setEmployeePositionId(e?.employeePositionId ?? "")
    setNote(e?.note ?? "")
  }

  const shifts = useQuery({
    queryKey: ["shifts", { size: 100 }],
    queryFn: () => api.organization.shifts.list({ size: 100 }),
    enabled: open,
  })

  const positions = useQuery({
    queryKey: ["employee", entry?.employeeId, "positions"],
    queryFn: () => api.employees.positions.list(entry!.employeeId),
    enabled: open && entry !== null,
  })

  const changed =
    entry !== null &&
    (shiftId !== entry.shiftId ||
      employeePositionId !== entry.employeePositionId ||
      note !== (entry.note ?? ""))

  const rebook = useMutation({
    mutationFn: () =>
      api.planning.updateEntry(entry!.id, {
        ...(shiftId !== entry!.shiftId ? { shiftId: Number(shiftId) } : {}),
        ...(employeePositionId !== entry!.employeePositionId
          ? { employeePositionId: Number(employeePositionId) }
          : {}),
        ...(note !== (entry!.note ?? "") ? { note: note.trim() || null } : {}),
      }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["shiftPlan"] })
      queryClient.invalidateQueries({ queryKey: ["attendance"] })
      toast.success(
        `${updated.employeeName} umgebucht: ${updated.shiftName} / ${updated.positionName}`
      )
      onOpenChange(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="rebook" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Planzeile umbuchen</SheetTitle>
          <SheetDescription>
            Schicht bzw. Stellenzuordnung der Planzeile ändern (G-13); Abwesende werden
            vom Backend geschützt.
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-4 py-2">
          {fixedEntry ? (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="font-medium">{fixedEntry.employeeName}</p>
              <p className="text-muted-foreground">
                {fmtDate(fixedEntry.date)} · {fixedEntry.shiftName} / {fixedEntry.positionName}
                {fixedEntry.workplaceName && ` · ${fixedEntry.workplaceName}`}
              </p>
            </div>
          ) : (
            <PlanEntryPicker date={date ?? ""} value={picked} onChange={onPick} />
          )}

          <div className="space-y-1.5">
            <Label>Neue Schicht</Label>
            <Select
              value={shiftId}
              disabled={!entry}
              onChange={(e) => setShiftId(e.target.value)}
            >
              {(shifts.data?.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Neue Stellenzuordnung</Label>
            <Select
              value={employeePositionId}
              disabled={!entry}
              onChange={(e) => setEmployeePositionId(e.target.value)}
            >
              {(positions.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.positionName ?? `#${p.positionId}`}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Notiz (optional)</Label>
            <Input
              value={note}
              disabled={!entry}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z. B. Ersatz für …"
            />
          </div>
        </div>
        <SheetFooter className="sm:justify-between">
          <Button
            variant="outline"
            disabled={!entry}
            onClick={() => {
              // Tausch übernimmt die aktuelle Planzeile; Umbuch-Sheet schließen,
              // damit keine Dialoge gestapelt werden.
              setSwapEntry(entry)
              onOpenChange(false)
            }}
          >
            <ArrowLeftRight className="mr-1 size-4" /> Tauschen …
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button
              disabled={!entry || !changed || rebook.isPending}
              onClick={() => rebook.mutate()}
            >
              Umbuchen
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>

      <SwapShiftDialog
        open={swapEntry !== null}
        onOpenChange={(o) => !o && setSwapEntry(null)}
        entry={swapEntry}
      />
    </Sheet>
  )
}
